import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useQuery } from "@apollo/client/react";

import { WAREHOUSE_LOCATIONS_DETAILS } from "../../constants/graphql/queries";

export default function WarehouseCard({ warehouse, onSelect }) {
  const { data, loading } = useQuery(WAREHOUSE_LOCATIONS_DETAILS, {
    variables: { warehouseId: warehouse.id },
    skip: !warehouse?.id,
    fetchPolicy: "cache-and-network",
  });

  const details = data?.warehouseLocationsDetails || {};
  const emptySlots = details.totalEmptyBaseLocations || 0;
  const occupiedSlots = details.totalOccupiedBaseLocations || 0;
  const totalSlots = emptySlots + occupiedSlots;

  const occupancyPercent =
    totalSlots === 0 ? "0%" : `${Math.round((occupiedSlots / totalSlots) * 100)}%`;

  return (
    <Card sx={{ height: "100%" }}>
      <CardActionArea
        onClick={() => onSelect(warehouse.id)}
        sx={{
          height: "100%",
          alignItems: "stretch",
        }}
      >
        <Box p={2.5} display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            gap={1.5}
          >
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <Box minWidth={0}>
                <Typography
                  fontSize={{ xs: 16, sm: 18 }}
                  fontWeight={500}
                  noWrap
                  title={warehouse.name}
                >
                  {warehouse.name}
                </Typography>
              </Box>
            </Box>

            {warehouse.warehouseId && (
              <Chip
                label={warehouse.warehouseId}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
            )}
          </Box>

          <Box flexGrow={1}>
            {loading && !data ? (
              <Stack spacing={1}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" />
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="#fff8db"
                    borderRadius="50%"
                    p={1}
                  >
                    <InsightsOutlinedIcon fontSize="small" htmlColor="#efbe25" />
                  </Box>
                  <Box>
                    <Typography fontSize={13} color="text.secondary">
                      Empty base locations
                    </Typography>
                    <Typography fontSize={15} fontWeight={500}>
                      {emptySlots.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="#f0eeec"
                    borderRadius="50%"
                    p={1}
                  >
                    <InsertChartOutlinedIcon fontSize="small" htmlColor="#6b625c" />
                  </Box>
                  <Box>
                    <Typography fontSize={13} color="text.secondary">
                      Occupied base locations
                    </Typography>
                    <Typography fontSize={15} fontWeight={500}>
                      {occupiedSlots.toLocaleString()} ({occupancyPercent})
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
